/**
 * Le scénario du test — des fans SCRIPTÉS, des flows simulés, aucun vrai fan.
 *
 * Décision de Kayla (18-08) : script fixe, pas de fan joué par un modèle. Tous
 * les candidats vivent exactement la même scène, sinon « bon » et « mauvais »
 * ne se comparent plus.
 *
 * ─── LA SCÈNE A CHANGÉ DE FORME (Kayla, 06-10-2026) ────────────────────────
 * « On les met en conditions réelles — c'est pas des vrais gens, une
 * simulation » : on leur demande d'ajouter des personnes à leur file, puis de
 * lancer un flow, le flow se réalise, puis d'envoyer le message à 50, puis de
 * créer une transaction. « À la fin, on doit pouvoir traquer : combien de temps
 * il a mis, est-ce qu'il a bien réalisé le test. »
 *
 * La scène du 19-08 (un fan déjà chauffé, l'ultimatum posé par Cyrano, puis
 * tarifs → proposition → explication → chauffe → transaction) testait une
 * conversation. Celle-ci teste le POSTE tel qu'il est depuis septembre : la
 * file qu'on remplit dans « Discussions », le flow « script 1 » qu'on lance sur
 * un NOUVEAU fan et qu'on laisse écrire, le prix qu'on annonce une fois le flow
 * fini, le lien qu'on envoie par le bouton $, et la main qu'on rend à Cyrano.
 * Cinq étapes, dans l'ordre où le chatteur les vit en shift.
 *
 * 🩸 ET TOUT CE QUE LA CRÉATRICE DIT EST CELUI DE LA PROD (06-10-2026, le
 * soir). Kayla : « t'as pas mis le vrai flow […] t'as pas mis le vrai message,
 * je sais pas pourquoi t'as pas regardé ce qu'on fait en prod. » Le flow, le
 * message à 50, les scripts sont repris MOT POUR MOT ; seules les répliques du
 * fan sont écrites pour le test.
 *
 * ⚠️ La détection de ce que le candidat écrit reste VOLONTAIREMENT indulgente
 * (on note s'il a compris le geste, pas sa formulation), et la scène ne coince
 * jamais : quand il patine, le fan relance, puis la scène avance en marquant
 * l'étape ratée. Il faut qu'il atteigne la transaction pour qu'on voie s'il
 * sait s'en servir.
 */

/* ── Les fans de la simulation ──────────────────────────────────────────── */

/** Une ligne du fil d'un fan, telle qu'elle est déjà là à l'ouverture. */
export interface LigneDOuverture {
  de: "fan" | "cyrano";
  texte: string;
}

export interface FanDuTest {
  id: string;
  nom: string;
  /**
   * NOUVEAU = arrivé aujourd'hui, n'a encore rien reçu. C'est à lui, et à lui
   * seul, qu'on lance « script 1 » — le critère `flow_lance` le vérifie.
   */
  nouveau: boolean;
  /** Ce que la ligne de « Discussions » affiche à droite (« à l'instant »). */
  ilYa: string;
  /** Le mot du signal, comme au poste (`pileDuChatteur.MOT_DU_SIGNAL`). */
  signal: "nouveau" | "répondu" | "à relancer";
  /** Ce qui est déjà dans le fil quand le candidat l'ouvre. */
  fil: readonly LigneDOuverture[];
}

/**
 * QUATRE fans libres dans « Discussions », dont DEUX nouveaux — la consigne de
 * Kayla, mot pour mot. Les deux anciens ont DÉJÀ reçu « script 1 » (le menu
 * des flows le grise « déjà reçu », comme au poste) : leur fil en porte le
 * début, mot pour mot celui de la prod.
 */
export const FANS: readonly FanDuTest[] = [
  {
    id: "lucas",
    nom: "Lucas",
    nouveau: true,
    ilYa: "à l'instant",
    signal: "nouveau",
    fil: [{ de: "fan", texte: "salut 😊 je viens de te trouver sur insta" }],
  },
  {
    id: "thomas",
    nom: "Thomas",
    nouveau: false,
    ilYa: "hier",
    signal: "à relancer",
    fil: [
      { de: "cyrano", texte: "je devrais pas te dire ça" },
      { de: "cyrano", texte: "mais t'es arrivé au mauvais moment, ou au bon" },
      { de: "fan", texte: "pk ?" },
      { de: "cyrano", texte: "attends" },
      { de: "cyrano", texte: "avant que je te raconte, t'es tranquille là ou pas" },
      { de: "fan", texte: "non là je bosse" },
      // La vraie branche « pas dispo » de la prod.
      { de: "cyrano", texte: "ok" },
      { de: "cyrano", texte: "je te garde ce que j'allais dire pour ce soir" },
      { de: "cyrano", texte: "tu me dis quand t'es tranquille" },
    ],
  },
  {
    id: "mehdi",
    nom: "Mehdi",
    nouveau: true,
    ilYa: "1 min",
    signal: "nouveau",
    fil: [{ de: "fan", texte: "coucou t'es dispo ?" }],
  },
  {
    id: "julien",
    nom: "Julien",
    nouveau: false,
    ilYa: "3 h",
    signal: "répondu",
    fil: [
      { de: "cyrano", texte: "je devrais pas te dire ça" },
      { de: "cyrano", texte: "mais t'es arrivé au mauvais moment, ou au bon" },
      { de: "fan", texte: "mdr" },
    ],
  },
];

export function fanParId(id: string | null): FanDuTest | null {
  return FANS.find((f) => f.id === id) ?? null;
}

/** Le plafond de la file — celui du serveur au poste (`PILE_PLAFOND`). */
export const PLAFOND_DE_LA_FILE = 15;

/* ── Les flows ──────────────────────────────────────────────────────────── */

/**
 * Un pas d'un flow simulé : ce que le FLOW envoie, ou la réponse du fan à un
 * « attends sa réponse », ou l'alerte qui dit au chatteur « à toi ».
 * `apresMs` = le délai depuis le pas précédent ; `indicateur` = ce que le fil
 * affiche pendant ce délai (« elle écrit… », « vidéo en cours d'envoi… »).
 */
export interface PasDuFlow {
  de: "flow" | "fan" | "alerte";
  texte: string;
  /** Un média envoyé par le flow — montré en vignette floutée, jamais lu. */
  media?: "video" | "image" | "vocal" | "gif";
  /** Ce que dit un vocal, quand la prod le sait (affiché sous la vignette). */
  transcription?: string;
  apresMs: number;
  indicateur?: string;
}

export interface FlowDuTest {
  cle: string;
  nom: string;
  pas: readonly PasDuFlow[];
}

/** Le délai entre deux textes du flow (2 s en prod, raccourci). */
const TEXTE = 1800;
/** Le délai avant la réponse du fan. */
const FAN = 2500;
/** Les longues pauses de la prod (90 à 110 s avant une vidéo), ramenées à 4 s. */
const PAUSE = 4000;

const fl = (texte: string, apresMs = TEXTE, plus: Partial<PasDuFlow> = {}): PasDuFlow => ({
  de: "flow",
  texte,
  apresMs,
  ...plus,
});
const fan = (texte: string): PasDuFlow => ({ de: "fan", texte, apresMs: FAN, indicateur: "il écrit…" });

/**
 * 🩸 « script 1 » — le VRAI flow d'accueil de Trista (prod, `messaging_flows`
 * 892c7724…, chemin heureux), MOT POUR MOT. Kayla, 06-10-2026 : « t'as pas
 * mis le vrai flow, t'as mis un flow un peu nul […] je sais pas pourquoi t'as
 * pas regardé ce qu'on fait en prod. » Le flow d'avant était inventé.
 *
 * Ce qui a changé par rapport à la prod, et seulement ça : les délais (2 s
 * entre deux textes → 1,8 s ; les pauses de 90–100 s avant une vidéo → 4 s,
 * avec « vidéo en cours d'envoi… ») et les médias, montrés en vignettes. Les
 * branches de la prod (« tfq ? », « t'es là ? », « pas dispo ») ne sont pas
 * jouées : le fan du test répond toujours. Ses répliques, elles, sont écrites
 * pour le test — courtes, style texto.
 */
export const SCRIPT_1: FlowDuTest = {
  cle: "script_1",
  nom: "script 1",
  pas: [
    fl("je devrais pas te dire ça", 1500),
    fl("mais t'es arrivé au mauvais moment, ou au bon"),
    fan("mdr pourquoi ?"),
    fl("attends"),
    fl("avant que je te raconte, t'es tranquille là ou pas"),
    fan("oui tranquille"),
    fl("je pense qu'au sexe depuis une heure"),
    fl("je te dirai pas pourquoi"),
    fl("j'ai la permission de te chauffer ou tu veux pas"),
    fan("vas-y chauffe moi 😏"),
    fl("bouge pas"),
    fl("", PAUSE, { media: "video", indicateur: "vidéo en cours d'envoi…" }),
    fl("voilà l'état dans lequel je suis"),
    fl("va falloir assumer maintenant"),
    fl("dis moi ce que tu me ferais là, maintenant"),
    fan("je t'embrasserais partout"),
    fl("continue"),
    fl("sois plus précis, j'veux des détails"),
    fan("je te plaque contre le mur et je t'embrasse dans le cou"),
    fl("tu m'excites de ouf la", TEXTE, { media: "vocal" }),
    fl("", PAUSE, { media: "video", indicateur: "vidéo en cours d'envoi…" }),
    fl("rgd dans quel état tu m'as mise"),
    fl("mais pour continuer, tu vas devoir me payer, en fait", TEXTE, { media: "gif" }),
    fl("", TEXTE, { media: "vocal", transcription: "tu continues ou on s'arrête là ?" }),
  ],
};

/**
 * « script 2 » — le second vrai flow de Trista (prod a1181dd3…), mot pour
 * mot, chemin heureux. Il est dans le menu parce qu'il y est au poste : c'est
 * le MAUVAIS flow pour un nouveau fan, et le lancer se note raté.
 */
export const SCRIPT_2: FlowDuTest = {
  cle: "script_2",
  nom: "script 2",
  pas: [
    fl("j'ai acheté un truc que je devrais pas mettre", 1500),
    fl("enfin, pas pour sortir"),
    fl("dis moi que t'es seul"),
    fl("sinon je me tais"),
    fan("si si je suis seul"),
    fl("bon j'ai un truc à te demander mais"),
    fl("faut que je sois sûre que t'es pas le genre à faire des captures"),
    fan("promis jamais"),
    fl("", PAUSE, { media: "image", indicateur: "photo en cours d'envoi…" }),
    fl("alors"),
    fl("je le garde ou je le rends"),
    fan("garde le 😏"),
    fl("tu l'as vu mis"),
    fl("dis moi comment tu me l'enlèverais"),
    fan("doucement, avec les dents"),
    fl("continue"),
    fl("sois plus précis, j'veux des détails"),
    fan("je te l'enlève en t'embrassant le cou"),
    fl("tu m'excites de ouf la", TEXTE, { media: "vocal" }),
    fl("", PAUSE, { media: "video", indicateur: "vidéo en cours d'envoi…" }),
    fl("rgd dans quel état tu m'as mise"),
    fl("mais pour continuer, tu vas devoir me payer, en fait"),
    fl("tu continues avec moi ou on s'arrête là ?"),
  ],
};

export const FLOWS: readonly FlowDuTest[] = [SCRIPT_1, SCRIPT_2];

export function flowParCle(cle: string | null): FlowDuTest | null {
  return FLOWS.find((f) => f.cle === cle) ?? null;
}

/** Combien de messages le FLOW envoie — c'est ce que compte son badge. */
export function messagesDuFlow(flow: FlowDuTest): number {
  return flow.pas.filter((p) => p.de === "flow").length;
}

/** La question sur laquelle le flow s'arrête — celle de l'alerte « à toi ». */
export function questionFinale(flow: FlowDuTest): string {
  const dernier = [...flow.pas].reverse().find((p) => p.de === "flow");
  return dernier?.transcription ?? dernier?.texte ?? "";
}

/** Ce que le fan répond à la question finale : il continue, il demande le prix. */
export const REPONSE_A_LA_FIN_DU_FLOW = "je continue 🥵 c'est combien ?";

/**
 * L'alerte de la prod à la fin du flow — le signal « à toi » du chatteur,
 * recopiée du message de prod (« {pseudo} a fini le script 1 et a répondu à
 * « tu continues ou on s'arrête là ? » : à toi. UN palier, le prix sur le
 * bouton, pas de menu. »).
 */
export function alerteDeFinDeFlow(pseudo: string, flow: FlowDuTest): string {
  return `${pseudo} a fini le ${flow.nom} et a répondu à « ${questionFinale(flow)} » : à toi. UN palier, le prix sur le bouton, pas de menu.`;
}

/* ── Ce qu'on vend : UN MÉDIA, jamais une formule ──────────────────────────
 *
 * 🩸 Kayla, 06-10-2026, le soir : « on ne propose pas ça. On vend un média —
 * [l'ancien nom de formule] ça veut rien dire, c'est un legacy de quand on
 * utilisait un menu. » Le menu d'offres est retiré depuis fin août ; le test
 * vendait encore une de ses formules. Il vend maintenant UN média précis du
 * Vault, comme au poste : dans « Nouvelle transaction », on choisit un
 * PRODUIT vidéo du catalogue, on met le prix, et on choisit LE MÉDIA dans
 * « Délivrer au paiement ».
 *
 * Et le média est celui du VRAI message de prod qui suit le flow (script
 * « Après le flow », message 0) : « laisse-moi te faire une vidéo de 15 min en
 * direct pour 50, je me gode de ouf 🥵 alors chaud bb ? ». Le plus petit est
 * celui du vrai script à 30 : « pour 30 je te montre comment je me doigte et
 * je me caresse le corps ».
 */

/** Un média du Vault de la copie — ce qu'on livre au paiement. */
export interface MediaDuVault {
  id: string;
  /** Le titre du média, tel qu'il apparaît dans le Vault. */
  titre: string;
  genre: "vidéo" | "photos";
  /** « vidéo de 15 min », « 5 photos ». */
  duree: string;
  /** Les produits du catalogue qui peuvent le vendre (le 1er = celui que
   *  Francis pré-remplit). */
  produits: readonly string[];
  /** Le prix auquel on le vend. */
  prixCents: number;
  /** Ce qu'on y voit, une ligne par élément — « le contenu de la bouteille ». */
  contenu: readonly string[];
}

/** LE média de la scène — la vidéo de 15 min, en direct, à 50 €. Un gode :
 *  « Vidéo jouet / à deux » ou « Vidéo solo » la vendent l'une et l'autre. */
export const VIDEO_A_50: MediaDuVault = {
  id: "v_direct_15",
  titre: "Vidéo en direct · 15 min",
  genre: "vidéo",
  duree: "vidéo de 15 min",
  produits: ["video_objet_duo", "video_solo"],
  prixCents: 5000,
  contenu: [
    "Une vidéo de 15 minutes, faite en direct, rien que pour lui",
    "Elle se gode jusqu'à jouir",
    "Elle gémit super bien",
  ],
};

/** Le média PLUS PETIT — la réponse à « tu me la fais à 30 ? ». */
export const VIDEO_A_30: MediaDuVault = {
  id: "v_doigts",
  titre: "Vidéo · doigts et caresses",
  genre: "vidéo",
  duree: "vidéo",
  produits: ["video_solo"],
  prixCents: 3000,
  contenu: ["Elle se doigte", "Elle se caresse le corps"],
};

/**
 * Le Vault de la copie : les deux médias de la scène, et deux autres. Les
 * deux autres ne sont pas du décor : « Délivrer au paiement » doit porter LE
 * média vendu, et un choix à un seul élément ne teste pas qu'il sait lequel.
 */
export const VAULT: readonly MediaDuVault[] = [
  VIDEO_A_50,
  VIDEO_A_30,
  {
    id: "v_douche",
    titre: "Sous la douche",
    genre: "vidéo",
    duree: "vidéo de 2 min",
    produits: ["video_solo"],
    prixCents: 2500,
    contenu: ["Elle se savonne sous la douche, de dos"],
  },
  {
    id: "p_selfies",
    titre: "Selfies du matin",
    genre: "photos",
    duree: "5 photos",
    produits: [],
    prixCents: 1500,
    contenu: ["Cinq selfies au réveil, en t-shirt"],
  },
];

export function mediaParId(id: string | null | undefined): MediaDuVault | null {
  return VAULT.find((m) => m.id === id) ?? null;
}

/** Un produit du catalogue global — ce que « Nouvelle transaction » choisit. */
export interface OffreCatalogue {
  cle: string;
  nom: string;
  /** La fourchette, telle que la liste l'affiche. */
  prixLabel: string;
  minCents: number;
  maxCents: number;
}

/**
 * LE CATALOGUE du test — un sous-ensemble du VRAI catalogue actif
 * (`offer_catalog_items`), clés, noms et fourchettes exacts : les produits
 * vidéo, et la séance de sexting. AUCUNE formule de l'ancienne carte — même là
 * où la prod y rattache encore ses boutons à 50 €, c'est le legacy que Kayla
 * ne veut plus voir.
 *
 * ⚠️ Pas de prix « de base » : on ne l'a pas relevé pour ces produits, et en
 * inventer un pré-remplirait le champ Prix d'un chiffre faux. Le champ reste
 * vide — c'est au candidat d'y mettre le prix CONCLU, et c'est noté.
 */
export const CATALOGUE: readonly OffreCatalogue[] = [
  { cle: "video_solo", nom: "Vidéo solo", prixLabel: "15–50 €", minCents: 1500, maxCents: 5000 },
  { cle: "video_objet_duo", nom: "Vidéo jouet / à deux", prixLabel: "30–60 €", minCents: 3000, maxCents: 6000 },
  { cle: "video_longue", nom: "Vidéo longue", prixLabel: "50–90 €", minCents: 5000, maxCents: 9000 },
  { cle: "sexting", nom: "Séance sexting", prixLabel: "15–30 €", minCents: 1500, maxCents: 3000 },
];

export function offreParCle(cle: string | null): OffreCatalogue | null {
  return CATALOGUE.find((o) => o.cle === cle) ?? null;
}

/**
 * LES SCRIPTS — les VRAIS dossiers de Trista (`chatting_script_messages`,
 * prod), textes MOT POUR MOT. Le candidat les insère depuis l'onglet
 * « Scripts » du panneau de droite — là où ils vivent au poste depuis le
 * 17-09-2026.
 *
 * Un message marqué `prixCents` est un BOUTON DE TRANSACTION au poste : il
 * ouvre « Nouvelle transaction » au prix indiqué (le lien seul part depuis le
 * 13-09, le texte reste le repère du chatteur).
 *
 * 🪦 06-10-2026 : partis avec l'ancienne carte, les dossiers qui vendaient
 * ses formules ; et les scripts de prod qui parlent encore d'une formule ou
 * du « menu » ne sont PAS repris — c'est le legacy que Kayla retire.
 */
export interface MessageDeScript {
  titre: string;
  texte: string;
  /** Bouton de transaction : le prix qu'il pose. */
  prixCents?: number;
}

export interface DossierScript {
  nom: string;
  messages: readonly MessageDeScript[];
}

export const SCRIPTS: readonly DossierScript[] = [
  {
    nom: "Après le flow",
    messages: [
      {
        titre: "le flow a dit « tu veux continuer ou on stoppe ? » → il demande combien",
        texte: "laisse-moi te faire une vidéo de 15 min en direct pour 50, je me gode de ouf 🥵 alors chaud bb ?",
      },
      {
        titre: "le lien à 50",
        texte: "et voilà pour toi mon bb, elle est assez longue pour te faire jouir 🥵 je gémis super bien, tu vas grave kiffer",
        prixCents: 5000,
      },
      { titre: "un cadeau pour conclure", texte: "si tu le prends bb je te donne une vidéo en cadeau" },
      { titre: "il ne répond plus", texte: "ne me laisse pas toute seule bb 🙄" },
    ],
  },
  {
    nom: "Proposer la vidéo et son prix (30 · 50 · 100)",
    messages: [
      { titre: "à 30", texte: "par contre ça ne serait pas gratuit bb, je t'envoie ça à 30 c'est bon ?" },
      { titre: "à 50, juste pour toi", texte: "je prendrai une vidéo juste pour toi bb mais à 50. paye et je le fais pour toi" },
      {
        titre: "50 au lieu de 100",
        texte: "c'est une vidéo de 15 min que je montre normalement à partir de 100, mais je te l'envoie à 50 et je t'ajoute un cadeau en plus parce que tu m'excites vraiment bb 🥵",
      },
      { titre: "le lien à 30", texte: "voilà bb, débloque et je t'envoie ça 🤤", prixCents: 3000 },
      { titre: "le lien à 50", texte: "voilà bb, juste pour toi. débloque et je t'envoie tout", prixCents: 5000 },
    ],
  },
  {
    nom: "Il hésite",
    messages: [
      { titre: "lui faire dire son prix", texte: "et tu peux combien bb ?" },
      { titre: "céder", texte: "d'accord, je le fais juste pour toi 🥺" },
      { titre: "proposer de réduire", texte: "si c'est trop pour toi je peux te le réduire bb" },
      { titre: "objection sur les frais", texte: "je t'ajoute une bonne vidéo en plus pour ça, c'est bon ?" },
      {
        titre: "il veut voir avant",
        texte: "bb je l'ai faite juste pour toi et je ne montre pas d'extrait ici. si tu veux voir, paye simplement",
      },
    ],
  },
  {
    nom: "Au moment du lien",
    messages: [
      { titre: "payer maintenant", texte: "paye maintenant et je te montre la vidéo" },
      { titre: "débloquer", texte: "débloque ça et je te régale bb" },
      { titre: "il paie", texte: "tu es un amour 😍 un instant bb" },
      { titre: "il a payé", texte: "ne craque pas en regardant ça 🤭" },
    ],
  },
];

/** Le VRAI message à 50 qui suit le flow — la bulle de « À faire maintenant »
 *  et le message de Francis. Lu dans `SCRIPTS`, jamais recopié. */
export const MESSAGE_A_50: string = SCRIPTS[0]!.messages[0]!.texte;

/* ── Les étapes ─────────────────────────────────────────────────────────── */

/**
 * Les temps de la scène, dans l'ordre — six gestes notés, plus `fini`.
 *
 * Le bandeau en annonce CINQ (`NUMERO_ETAPE`) : Kayla en a dit cinq — la file,
 * le flow, le flow qui se réalise, le message à 50, la transaction — et
 * « Reprendre » ferme la cinquième. Le moteur, lui, les sépare : rendre la main
 * est un geste à part, qu'on note à part.
 *
 * 🪦 Les temps de la scène du 19-08 (`tarifs`, `expliquer`, `chauffer`) sont
 * partis le 06-10-2026 avec elle. Les fiches déjà passées ne bougent pas : la
 * fiche admin garde leurs scènes dans un registre mort (`CandidateFiche`).
 */
export type Etape =
  | "file"
  | "flow"
  | "flow_en_cours"
  | "proposer"
  /** Le fan trouve ça cher (06-10-2026) : tenir la valeur, ou changer de média. */
  | "negocier"
  | "transaction"
  | "rendre"
  | "fini";

export type EtapeJouee = Exclude<Etape, "fini">;

export const ETAPES: readonly Etape[] = [
  "file",
  "flow",
  "flow_en_cours",
  "proposer",
  "negocier",
  "transaction",
  "rendre",
  "fini",
];

/** Le numéro que le bandeau affiche — « Étape N/5 ». */
export const NUMERO_ETAPE: Record<EtapeJouee, number> = {
  file: 1,
  flow: 2,
  flow_en_cours: 3,
  proposer: 4,
  negocier: 4,
  transaction: 5,
  rendre: 5,
};

export const NOMBRE_D_ETAPES = 5;

/** Le titre court de chaque numéro, pour le rail du bandeau. */
export const TITRES_DU_RAIL: readonly string[] = [
  "La file",
  "Le flow",
  "Le flow tourne",
  "La vente à 50",
  "La transaction",
];

/**
 * La consigne de chaque temps — ce que le bandeau dit en gros.
 *
 * ⚠️ Elle dit le GESTE ATTENDU, jamais le verdict (décision du 18-08 : la
 * machine ne commente pas en direct ; la note est à Kayla).
 *
 * 🪤 Registre tenu par son test (`registres.test.ts`) : un temps ajouté à
 * `ETAPES` sans consigne ici fait rougir, et une consigne orpheline aussi.
 */
export const LIBELLES_ETAPES: Record<EtapeJouee, string> = {
  file: "Ajoute les nouveaux fans à ta file",
  flow: "Ouvre un nouveau fan et lance le flow « script 1 »",
  flow_en_cours: "Le flow écrit à ta place — laisse-le finir, n'écris pas",
  proposer: "Propose-lui ta vidéo à 50 € : dis ce qu'il y a dedans, et que tu la fais pour lui",
  negocier: "Il trouve ça cher — tiens ton prix, ou propose-lui un média plus petit",
  transaction: "Envoie-lui le lien de paiement avec le bouton $ (Vente)",
  rendre: "C'est payé — rends la main à Cyrano avec Reprendre",
};

/* ── Le coup de pouce : ce qu'on lui montre du doigt ─────────────────────── */

/** Les choses que l'écran sait entourer d'un rond (cf. `Halo`). */
export type Cible =
  | "discussions"
  | "ma_file"
  | "a_faire"
  | "champ"
  | "vente"
  | "reprendre";

export interface Pouce {
  /** `null` = rien à montrer du doigt — il faut attendre. */
  cible: Cible | null;
  /** UNE ligne, qui dit OÙ cliquer — le bandeau dit déjà QUOI faire. */
  texte: string;
}

/**
 * UN coup de pouce par temps — le petit rond du tutoriel (Kayla, 19-08 :
 * « un petit rond, comme dans un jeu »). Il désigne un bouton et nomme le
 * geste ; il ne dit JAMAIS quoi écrire.
 */
export const COUPS_DE_POUCE: Record<EtapeJouee, Pouce> = {
  file: {
    cible: "discussions",
    texte: "Ouvre l'onglet « Discussions » et touche « Ajouter » sur chaque fan marqué NOUVEAU",
  },
  flow: {
    cible: "a_faire",
    texte: "Ouvre un nouveau fan dans « Ma file » : « À faire maintenant » te propose son flow",
  },
  flow_en_cours: {
    cible: null,
    texte: "Le flow envoie ses messages tout seul — attends « Flow terminé »",
  },
  proposer: {
    cible: "champ",
    texte: "Ce que contient la vidéo est dans l'onglet « Vault », à droite — décris-la-lui",
  },
  negocier: {
    cible: "champ",
    texte: "Même vidéo : 40 € au plus bas. En dessous, un autre média (onglet « Vault »)",
  },
  transaction: {
    cible: "vente",
    texte: "Touche $ : un produit vidéo, le prix conclu, et LA vidéo dans « Délivrer au paiement »",
  },
  rendre: {
    cible: "reprendre",
    texte: "Touche « Reprendre », en haut de la conversation, à côté de Cyrano",
  },
};

/** Le coup de pouce du temps courant — `null` une fois la scène close. */
export function coupDePouce(etape: Etape): Pouce | null {
  return etape === "fini" ? null : COUPS_DE_POUCE[etape];
}

/* ── Ce que le fan dit ──────────────────────────────────────────────────── */

/**
 * La patience du fan, temps par temps — combien de faux pas avant que la scène
 * avance sans lui, et ce que le fan dit entre deux.
 *
 * Seuls trois temps en ont une : ce sont ceux où le candidat ÉCRIT à la place
 * du geste attendu. Ajouter des fans et laisser tourner un flow n'ont pas de
 * faux pas qu'un fan relèverait ; rendre la main non plus — le fan a payé, il
 * se tait, et on laisse au candidat trois essais avant de clore pour lui.
 *
 * ⛔ Une relance répond au geste RATÉ, jamais au suivant : une réplique qui
 * anticipe lui ferait sauter une étape (leçon du 19-08).
 */
export interface Patience {
  relances: readonly string[];
  /** Au N-ième faux pas, la scène avance de force. */
  essais: number;
  /** Ce que le fan dit quand elle avance — `null` = il se tait. */
  avance: string | null;
}

export const PATIENCE: Partial<Record<EtapeJouee, Patience>> = {
  flow: {
    relances: ["?", "allo ? t'es là ?"],
    essais: 3,
    avance: "bon… t'as quoi à me proposer ? c'est combien ?",
  },
  proposer: {
    relances: ["alors tu me proposes quoi ?", "un truc précis avec son prix stp, j'vais pas deviner"],
    essais: 3,
    avance: "bon ta vidéo à 50 j'la prends, envoie-moi le lien",
  },
  negocier: {
    relances: ["alors ? 30 ça te va ou pas ?", "réponds bb, tu me la fais à 30 ?"],
    essais: 3,
    avance: "bon laisse tomber, j'prends à 50, envoie le lien",
  },
  transaction: {
    relances: ["bah envoie-moi le lien alors", "tu m'envoies le truc à payer ?"],
    essais: 3,
    avance: "j'attends toujours le lien là… bon tant pis, une autre fois",
  },
  rendre: {
    relances: [],
    essais: 3,
    avance: null,
  },
};
/**
 * L'OBJECTION — ce que le fan répond à la vidéo à 50 €.
 *
 * Kayla, 06-10-2026 : « on a mis 50 : avec les frais, on peut baisser à 40,
 * mais si on baisse à 30, on ne peut pas lui vendre le même média qu'on lui a
 * proposé à 50. » Le fan demande donc 30 — le prix qui force le choix.
 */
export const OBJECTION_DU_FAN: readonly string[] = [
  "mmh ça a l'air chaud",
  "mais 50 c'est cher… tu me la fais à 30 ?",
];

/** Ce que le fan dit quand le candidat tient 50 la PREMIÈRE fois : il insiste
 *  une fois, puis il cède (`FAN_CEDE_A_50`). */
export const FAN_INSISTE: string = "t'es dure 😅 elle est vraiment faite que pour moi ?";
export const FAN_CEDE_A_50: string = "bon ok va pour 50, envoie le lien";

/** Ce que le fan répond à la position du candidat. */
export function fanAccepte(accord: { mediaId: string; prixCents: number }): string {
  const prix = `${accord.prixCents / 100}`;
  if (accord.mediaId !== VIDEO_A_50.id) return `ok va pour celle-là à ${prix}, envoie le lien`;
  return accord.prixCents < PLANCHER_DE_LA_VIDEO_CENTS
    ? `ah top 😏 envoie à ${prix} alors`
    : `ok va pour ${prix}, envoie le lien 🔥`;
}

/** Ce que le fan dit après avoir payé. */
export const APRES_LE_PAIEMENT: readonly string[] = ["c'est payé 💸 j'ai hâte de voir ça"];

// ─── Détection : a-t-il fait le geste ? ─────────────────────────────────────

const sansAccent = (s: string) =>
  s.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();

/**
 * La frappe est-elle un `/menu` — la commande RETIRÉE ?
 *
 * 🪦 Ce détecteur servait à ENVOYER la carte. Depuis le 29-08-2026 il sert à la
 * refuser : la commande n'existe plus au poste, et un candidat qui la tape doit
 * lire la même phrase qu'il lirait dans le vrai composeur, pas voir « /menu »
 * partir au fan comme un message ordinaire.
 *
 * 🩸 Il reste insensible à la casse, et ça n'est pas décoratif. Kayla, 19-08 :
 * « j'ai fait /menu et le menu, il ne s'est pas envoyé. » Le composeur comparait
 * `texte === "/menu"` quand le dropdown, lui, testait sans la casse — une
 * majuscule de clavier mobile suffisait à faire diverger les deux. Le refus
 * doit couvrir exactement ce que le candidat peut taper, sinon on retombe sur
 * un « /menu » posé dans le fil, sans explication.
 *
 * ⚠️ `/menu game` n'entre PAS ici : la roue du jeu, elle, part toujours au
 * poste. Elle n'a simplement rien à faire dans cette scène-ci, donc elle reste
 * un message ordinaire — comme avant.
 */
export function estLaCommandeMenu(texte: string): boolean {
  return texte.trim().toLowerCase() === "/menu";
}

/**
 * Il propose UNE offre, avec son prix — le geste qui remplace la carte.
 *
 * Kayla, 29-08-2026, sur ce qu'on attend désormais d'un chatteur : une offre,
 * celle qui colle à CE fan, avec son prix. Pas la liste.
 *
 * Trois conditions, et la troisième est le vrai sujet :
 *   ① il NOMME quelque chose de vendable (« une vidéo », « des nudes ») ;
 *   ② il donne un PRIX ;
 *   ③ il n'en donne pas plus de deux.
 *
 * ⛔ Le plafond de ③ est la moitié de l'épreuve. Un candidat qui recopie les
 * six lignes du menu à la main refait EXACTEMENT ce que Kayla vient de retirer
 * — en pire, puisque rien ne tient plus ses prix à jour. Sans ce plafond,
 * l'épreuve continuerait de récompenser le catalogue déballé, et on aurait
 * changé la commande sans changer ce qu'on recrute.
 *
 * 🪶 Deux prix passent, exprès : « 50 au lieu de 60 » est une remise, et Kayla
 * l'autorise explicitement sur un fan pour conclure maintenant. C'est trois qui
 * fait la liste.
 *
 * ⚠️ Indulgent sur la FORME, comme ses voisins : on ne note pas une
 * formulation, on note s'il a compris le geste.
 */
export function proposeUneOffre(texte: string): boolean {
  const t = sansAccent(texte);
  // Racines, jamais de mots bornés des deux côtés — même leçon que
  // `expliqueLOffre` : le `s` du pluriel casse une frontière de mot.
  const nommeQuelqueChose =
    /\bphoto\w*|\bvideo\w*|\bvid\b|\bnude\w*|\bsexting\w*/.test(
      t,
    );
  // Un prix : un nombre porté par une unité (« 50 € », « 50 euros », « 50
  // balles »), ou annoncé par ce qui précède un prix (« à 50 », « c'est 50 »).
  const prix = t.match(
    /\b\d{1,3}\s*(?:€|eur\w*|balle\w*|e\b)|(?:\ba\b|\bpour\b|\bc'?est\b|\bprix\b)\s*(?:de\s*)?\d{2,3}\b/g,
  );
  if (!nommeQuelqueChose || !prix) return false;
  return prix.length <= 2;
}


/**
 * Il propose SA vidéo, à 50 € — le message attendu après le flow.
 *
 * Le vrai message de prod : « laisse-moi te faire une vidéo de 15 min en
 * direct pour 50, je me gode de ouf 🥵 alors chaud bb ? ». C'est
 * `proposeUneOffre` (un média nommé, un prix, pas la liste) PLUS ce qui fait
 * que c'est CETTE vidéo : il annonce 50.
 */
export function proposeLaVideo(texte: string): boolean {
  if (!proposeUneOffre(texte)) return false;
  return /\b50\b/.test(sansAccent(texte));
}

/* ── La stratégie de vente (Kayla, 06-10-2026) ──────────────────────────────
 *
 * « L'idée qu'on doit savoir quel média on vend. On doit lui faire penser que
 * la vidéo a été faite en direct, spécialement pour lui. On doit vendre cher.
 * […] Si on baisse à 30, on ne peut pas lui vendre le même média qu'on lui a
 * proposé à 50. On doit donner de la valeur à ce qu'on vend. C'est important
 * de connaître le contenu de la bouteille pour pouvoir vendre. »
 *
 * Quatre règles, et leurs détecteurs : il DIT ce qu'il y a dedans
 * (`decritLeContenu`), il fait sentir que c'est fait POUR LUI, en direct
 * (`faitPourLui`), il TIENT la valeur — 40 € au plus bas pour la même vidéo —
 * ou il change de média (`positionDeNegociation`).
 *
 * ⚠️ Indulgents sur la forme, comme leurs voisins : sans accents, fautes de
 * clavier courantes (« ke », « pr », « tw ») ; on note le geste, pas la phrase.
 */

/** 40 € : le plus bas pour la MÊME vidéo (« avec les frais, on peut baisser à
 *  40 »). En dessous, c'est un autre média, plus petit — jamais le même. */
export const PLANCHER_DE_LA_VIDEO_CENTS = 4000;

/** Un élément du contenu qu'on sait reconnaître dans ce qu'il écrit. */
export interface ElementDuContenu {
  nom: string;
  motif: RegExp;
}

/** Ce que la note reconnaît du contenu de chaque média de la scène. */
export const ELEMENTS_DU_CONTENU: Record<string, readonly ElementDuContenu[]> = {
  [VIDEO_A_50.id]: [
    { nom: "15 min", motif: /\b15\s*(min|mn)|quinze min/ },
    { nom: "le gode", motif: /\bgode|godemich|\bjouet|sex ?toy|dildo/ },
    { nom: "jusqu'à jouir", motif: /jouir|\bjoui|orgasm|jusqu'?au bout/ },
    { nom: "elle gémit", motif: /gemi/ },
  ],
  [VIDEO_A_30.id]: [
    { nom: "elle se doigte", motif: /doigt/ },
    { nom: "elle se caresse", motif: /caress/ },
  ],
};

/** Le texte normalisé : sans accents, minuscules, fautes de clavier courantes
 *  ramenées à leur mot (« ke » → que, « pr » → pour, « tw » → toi). */
function normaliser(texte: string): string {
  return sansAccent(texte)
    .replace(/[’`]/g, "'")
    .replace(/\s+/g, " ")
    .replace(/\bke\b/g, "que")
    .replace(/\bpr\b/g, "pour")
    .replace(/\b(toa|twa|tw)\b/g, "toi")
    .replace(/\bfai\b/g, "fais");
}

/** Les éléments du contenu de ce média qu'il a NOMMÉS dans ces messages. */
export function elementsDits(messages: readonly string[], mediaId: string): string[] {
  const t = normaliser(messages.join(" \n "));
  return (ELEMENTS_DU_CONTENU[mediaId] ?? []).filter((e) => e.motif.test(t)).map((e) => e.nom);
}

/** « Il dit ce qu'il y a dedans » : au moins deux éléments du contenu. */
export function decritLeContenu(messages: readonly string[], mediaId: string): boolean {
  return elementsDits(messages, mediaId).length >= 2;
}

/** « Fait pour lui, en direct » — tournée MAINTENANT, rien que pour lui. */
const POUR_LUI =
  /(rien|juste|que|specialement|special|expres|exclusivement)( que)? pour toi|pour toi (tout )?seul|faite? (rien )?(que )?pour toi|(tournee?|filmee?|enregistree?|personnalisee?) (rien )?(que )?pour toi|je (te )?(la|le) (fais|fait|tourne|filme)\b|je viens de (la |te la )?(faire|tourner|filmer)|je (l'?ai|lai|te l'?ai) (faite?|tournee?|filmee?)|en direct|en live|\ba l'?instant\b|\bla maintenant\b|tout de suite pour toi/;

/** Le passage qui fait croire que c'est fait pour lui, en direct — ou null. */
export function faitPourLui(texte: string): string | null {
  const m = normaliser(texte).match(POUR_LUI);
  return m ? m[0] : null;
}

/** Ce qu'il dit de la vidéo PLUS PETITE (doigts, caresses, « une autre »). */
const AUTRE_MEDIA =
  /doigt|caress|autre (video|chose|truc|media)|plus petite?|moins (longue?|forte?)\b|une plus courte|video plus courte/;

/** Ce qui TIENT le prix sans le dire : refuser, ajouter un cadeau, faire payer. */
const TIENT =
  /\bnon\b|peux pas|pas moins|impossible|c'?est (le|mon) prix|\bvaut\b|pas de (reduc|promo)|je baisse pas|baisser pas|en plus|cadeau|bonus|ajoute|\bpaye|\bpaie\b|debloque/;

/** Les prix qu'il annonce — 2 ou 3 chiffres, pas une durée, pas nié
 *  (« pas 30 »), en centimes. */
export function prixAnnonces(texte: string): number[] {
  const t = normaliser(texte);
  const out: number[] = [];
  for (const m of t.matchAll(/(\d{2,3})(?:[.,]\d{1,2})?(?!\d)/g)) {
    const i = m.index ?? 0;
    const avant = t.slice(Math.max(0, i - 10), i);
    const apres = t.slice(i + m[0].length, i + m[0].length + 8);
    if (/^\s*(min\b|minute|mn\b|ans\b|sec|s\b|h\b|nude|photo|video|fois)/.test(apres)) continue;
    if (/(pas|non|jamais|plus)\s*(a\s*)?$/.test(avant)) continue;
    const n = Number(m[1]);
    if (n >= 5 && n <= 300) out.push(n * 100);
  }
  return out;
}

/** La position du candidat face à « tu me la fais à 30 ? ». */
export type Position =
  | { genre: "meme"; prixCents: number }
  | { genre: "autre"; prixCents: number }
  | { genre: "refus" }
  | null;

/**
 * Ce qu'il répond à l'objection, lu dans UN message :
 *   · il parle de la vidéo PLUS PETITE (« je me doigte », « une autre
 *     vidéo ») → autre, à son prix annoncé ou à 30 € ;
 *   · il annonce un prix → la même vidéo, à ce prix (le plus bas annoncé :
 *     « 40 au lieu de 50 » vaut 40) ;
 *   · il tient sans prix (« non », « je t'ajoute une bonne vidéo en plus »,
 *     « paye simplement ») → il tient 50 ;
 *   · il dit oui sans prix (« ok », « d'accord ») → il accepte les 30 du fan ;
 *   · rien de tout ça → null : le fan relance.
 */
export function positionDeNegociation(texte: string): Position {
  const t = normaliser(texte);
  const prix = prixAnnonces(texte);
  const plusBas = prix.length ? Math.min(...prix) : null;
  if (AUTRE_MEDIA.test(t)) return { genre: "autre", prixCents: plusBas ?? VIDEO_A_30.prixCents };
  if (plusBas !== null) return { genre: "meme", prixCents: plusBas };
  if (TIENT.test(t)) return { genre: "refus" };
  if (/\b(ok|oki|okay|oui|ouais|d'?accord|dac|vas[- ]?y|ca marche|deal|va pour|ca me va|tope)\b/.test(t)) {
    return { genre: "meme", prixCents: 3000 };
  }
  return null;
}
